/**
 * Color palette definitions for light and dark themes
 * Centralized color management for easy theme switching
 */

export interface ColorPalette {
  // Primary colors
  primary: string;
  primaryDark: string;
  primaryLight: string;
  
  // Gradient colors
  gradient: {
    primaryStart: string;
    primaryEnd: string;
  };
  
  // Button colors
  button: {
    border: string;
    text: string;
    gradientStart: string;
    gradientEnd: string;
  };
  
  // Bank details colors
  bankDetails: {
    text: string;
  };
  
  // Confirmation page colors
  confirmation: {
    submitBg: string;
    submitBorder: string;
    cancelBg: string;
    cancelBorder: string;
  };
  
  // Offline modal colors
  offline: {
    primaryBg: string;
    secondaryBorder: string;
    secondaryText: string;
  };
  
  // Secondary colors
  secondary: string;
  secondaryLight: string;
  secondaryDark: string;
  
  // Status colors
  success: string;
  successLight: string;
  successDark: string;
  
  error: string;
  errorLight: string;
  errorDark: string;
  
  warning: string;
  warningLight: string;
  warningDark: string;
  warningRed: string;
  
  info: string;
  infoLight: string;
  infoBlue: string;
  infoDark: string;
  
  // Neutral colors
  dark: string;
  grayDarkerFont: string;
  grayDark: string;
  gray: string;
  grayLight: string;
  grayLimitFont: string;
  grayLighter: string;
  light: string;
  white: string;
  
  // Text colors
  text: {
    primary: string;
    secondary: string;
    light: string;
    muted: string;
    error: string;
    label: string;
    inputColor: string;
  };
  
  // Background colors
  bgPrimary: string;
  bgSecondary: string;
  bgDark: string;
  bgLight: string;
  accordionBg: string;

  // Checkbox colors
  checkedColor: string;
  checkedTickColor: string;
}

export const lightColors: ColorPalette = {
  // Premium Primary Colors - Purple/Blue brand
  primary: '#8064E6',
  primaryDark: '#6941C6',
  primaryLight: '#9D8AEC',
  
  gradient: {
    primaryStart: '#8064E6',
    primaryEnd: '#8064E6',
  },

  checkedColor: 'rgb(244, 246, 253)',
  checkedTickColor: '#2C2C2C',
  
  // Button colors
  button: {
    border: env.light.buttonBorder || 'rgb(128, 100, 230)',
    text: env.light.buttonText || '#FFFFFF',
    gradientStart: env.light.buttonGradientStart || '#8064E6',
    gradientEnd: env.light.buttonGradientEnd || '#8064E6',
  },
  
  // Bank details colors
  bankDetails: {
    text: env.light.bankDetailsText || 'rgb(127, 104, 235)',
  },
  
  // Confirmation page colors
  confirmation: {
    submitBg: env.light.confirmationSubmitBg || 'rgb(127, 104, 235)',
    submitBorder: env.light.confirmationSubmitBorder || 'rgb(128, 100, 230)',
    cancelBg: env.light.confirmationCancelBg || 'rgb(244, 246, 253)',
    cancelBorder: env.light.confirmationCancelBorder || 'rgb(216, 221, 236)',
  },
  
  // Offline modal colors
  offline: {
    primaryBg: env.light.offlinePrimaryBg || 'rgb(255, 0, 34)',
    secondaryBorder: env.light.offlineSecondaryBorder || 'rgb(128, 100, 230)',
    secondaryText: env.light.offlineSecondaryText || 'rgb(128, 100, 230)',
  },
  
  secondary: '#003366',
  secondaryLight: '#1a4d80',
  secondaryDark: '#002244',
  
  // Premium Success Colors - Exact match
  success: '#067647', // Premium green
  successLight: '#d4edda',
  successDark: '#067647',
  
  // Premium Error Colors - Exact match
  error: '#d92d20', // Premium red
  errorLight: '#FEF3F2', // Premium error background
  errorDark: '#B42318', // Premium error dark
  
  warning: '#ffc107',
  warningLight: '#fff3cd',
  warningDark: '#e0a800',
  warningRed: '#ff0022',
  
  info: '#17a2b8',
  infoLight: '#d1ecf1',
  infoBlue: 'rgb(24, 107, 232)', // Premium blue
  infoDark: '#138496',
  
  // Premium Neutral Colors - Exact match
  dark: '#6c757d',
  grayDarkerFont: '#1F2937',
  grayDark: '#495057',
  gray: '#6c757d',
  grayLight: 'rgb(216, 221, 236)', // #D8DDEC - Premium border color
  grayLimitFont: '#4e5d6c',
  grayLighter: '#F1F3F9',
  light: '#F4F0FF',
  white: 'rgb(255, 255, 255)', // #FFFFFF
  
  // Premium Text Colors - Exact match
  text: {
    primary: '#1F2937',
    secondary: '#8064E6',
    light: '#FFFFFF',
    muted: 'rgb(125, 125, 125)', // #7D7D7D - Premium muted text
    error: '#d92d20',
    label: '#1F2937',
    inputColor: 'rgb(125, 125, 125)', // #7D7D7D
  },
  
  // Premium Background Colors - Exact match
  bgPrimary: 'rgb(255, 255, 255)', // #FFFFFF - Card background
  bgSecondary: '#F4F6FD',
  bgDark: '#212529',
  bgLight: '#F4F0FF',
  accordionBg: 'rgb(255, 255, 255)', // Light white accordion background for light theme
};

export const africaBetLightColors: ColorPalette = {
  primary: '#289BF6',
  primaryDark: '#1A7AC7',
  primaryLight: '#5FB4F9',

  gradient: {
    primaryStart: '#289BF6',
    primaryEnd: '#5FB4F9',
  },

  checkedColor: 'rgb(244, 246, 253)',
  checkedTickColor: '#2C2C2C',

  button: {
    border: '#289BF6',
    text: '#FFFFFF',
    gradientStart: '#289BF6',
    gradientEnd: '#289BF6',
  },

  bankDetails: {
    text: '#289BF6',
  },

  confirmation: {
    submitBg: '#289BF6',
    submitBorder: '#289BF6',
    cancelBg: 'rgb(244, 246, 253)',
    cancelBorder: '#E3E7F1',
  },

  offline: {
    primaryBg: 'rgb(255, 0, 34)',
    secondaryBorder: '#289BF6',
    secondaryText: '#289BF6',
  },

  secondary: '#003366',
  secondaryLight: '#1A4D80',
  secondaryDark: '#002244',

  success: '#067647',
  successLight: '#D4EDDA',
  successDark: '#067647',

  error: '#D92D20',
  errorLight: '#FEF3F2',
  errorDark: '#B42318',

  warning: '#FFC107',
  warningLight: '#FFF3CD',
  warningDark: '#E0A800',
  warningRed: '#FF0022',

  info: '#17A2B8',
  infoLight: '#D1ECF1',
  infoBlue: '#289BF6',
  infoDark: '#138496',

  dark: '#6C757D',
  grayDarkerFont: '#2D2D3F',
  grayDark: '#495057',
  gray: '#6C757D',
  grayLight: '#E3E7F1',
  grayLimitFont: '#4E5D6C',
  grayLighter: '#E9ECEF',
  light: 'rgb(244, 246, 253)',
  white: '#FFFFFF',

  text: {
    primary: '#2D2D3F',
    secondary: '#289BF6',
    light: '#FFFFFF',
    muted: 'rgb(125, 125, 125)',
    error: '#D92D20',
    label: '#2D2D3F',
    inputColor: 'rgb(125, 125, 125)',
  },

  bgPrimary: '#FFFFFF',
  bgSecondary: 'rgb(244, 246, 253)',
  bgDark: '#212529',
  bgLight: '#E8F4FE',
  accordionBg: '#FFFFFF',
};

/**
 * Dark mode colors loaded from environment variables
 * Brand-specific themes (standard = purple/blue, lf = teal/gold)
 */

import { env } from '@/config/env';

// Brand-specific fallback colors
const standardBrandDefaults: ColorPalette = {
  // Standard Brand - Purple/Blue
  primary: '#9D8AEC',
  primaryDark: '#8064E6',
  primaryLight: '#B5A3F0',

  gradient: {
    primaryStart: '#9D8AEC',
    primaryEnd: '#9B95FF',
  },

  // Button colors
  button: {
    border: '#9D8AEC',
    text: '#FFFFFF',
    gradientStart: '#9D8AEC',
    gradientEnd: '#9B95FF',
  },

  // Bank details colors
  bankDetails: {
    text: '#B5A3F0',
  },

  // Confirmation page colors
  confirmation: {
    submitBg: '#9D8AEC',
    submitBorder: '#8064E6',
    cancelBg: '#2C2C2C',
    cancelBorder: '#364152',
  },

  // Offline modal colors
  offline: {
    primaryBg: 'rgb(255, 0, 34)',
    secondaryBorder: '#9D8AEC',
    secondaryText: '#B5A3F0',
  },

  checkedColor: '#9D8AEC',
  checkedTickColor: '#FFFFFF',

  secondary: '#4A90E2',
  secondaryLight: '#6BA8F0',
  secondaryDark: '#2E6BB8',

  success: '#4CAF50',
  successLight: '#2d4a2f',
  successDark: '#66BB6A',

  error: '#EF5350',
  errorLight: '#4a2d2e',
  errorDark: '#F44336',

  warning: '#FFB74D',
  warningLight: '#4a3d2d',
  warningDark: '#FFA726',
  warningRed: '#ff4466',

  info: '#29B6F6',
  infoLight: '#2d3d4a',
  infoBlue: '#42A5F5',
  infoDark: '#03A9F4',

  dark: '#ffffff',
  grayDarkerFont: '#ffffff',
  grayDark: '#ffffff',
  gray: '#ffffff',
  grayLight: '#364152',
  grayLimitFont: '#ffffff',
  grayLighter: '#424242',
  light: '#2C2C2C',
  white: '#1E1E1E',

  text: {
    primary: '#ffffff',
    secondary: '#B5A3F0',
    light: '#ffffff',
    muted: '#ffffff',
    error: '#EF5350',
    label: '#ffffff',
    inputColor: '#CDD5DF',
  },

  bgPrimary: '#121926',
  bgSecondary: '#121926',
  bgDark: '#ffffff',
  bgLight: '#2d2540',
  accordionBg: '#0D121C',
};

const lfBrandDefaults: ColorPalette = {
  // LF Brand - Teal/Gold
  primary: '#8B7D2F',
  primaryDark: '#6B5D1F',
  primaryLight: '#9B8D3F',

  gradient: {
    primaryStart: '#8B7D2F',
    primaryEnd: '#9B8D3F',
  },

  // Button colors
  button: {
    border: '#8B7D2F',
    text: '#FFFFFF',
    gradientStart: '#8B7D2F',
    gradientEnd: '#9B8D3F',
  },

  // Bank details colors
  bankDetails: {
    text: '#C9A841',
  },

  // Confirmation page colors
  confirmation: {
    submitBg: '#C9A841',
    submitBorder: '#B8982E',
    cancelBg: '#1A4444',
    cancelBorder: '#2D5555',
  },

  // Offline modal colors
  offline: {
    primaryBg: 'rgb(255, 0, 34)',
    secondaryBorder: '#C9A841',
    secondaryText: '#C9A841',
  },

  checkedColor: '#8B7D2F',
  checkedTickColor: '#FFFFFF',

  secondary: '#C9A841',
  secondaryLight: '#FFD700',
  secondaryDark: '#9B8B3D',

  success: '#4CAF50',
  successLight: '#2d4a2f',
  successDark: '#66BB6A',

  error: '#EF5350',
  errorLight: '#4a2d2e',
  errorDark: '#F44336',

  warning: '#FFD700',
  warningLight: '#FFF8DC',
  warningDark: '#C9A841',
  warningRed: '#ff4466',

  info: '#FFD700',
  infoLight: '#2d3d4a',
  infoBlue: '#C9A841',
  infoDark: '#9B8B3D',

  dark: '#FFFFFF',
  grayDarkerFont: '#FFFFFF',
  grayDark: '#E0E0E0',
  gray: '#C0C0C0',
  grayLight: '#2D5555',
  grayLimitFont: '#FFD700',
  grayLighter: '#1A4444',
  light: '#103333',
  white: '#0A2F2F',

  text: {
    primary: '#FFFFFF',
    secondary: '#C9A841',
    light: '#FFFFFF',
    muted: '#B0B0B0',
    error: '#EF5350',
    label: '#C9A841',
    inputColor: '#7D9999',
  },

  bgPrimary: '#0D3535',
  bgSecondary: '#0A2F2F',
  bgDark: '#FFFFFF',
  bgLight: '#1A4848',
  accordionBg: '#0A2828',
};

const africaBetBrandDefaults: ColorPalette = {
  primary: '#289BF6',
  primaryDark: '#1A7AC7',
  primaryLight: '#5FB4F9',

  gradient: {
    primaryStart: '#289BF6',
    primaryEnd: '#5FB4F9',
  },

  button: {
    border: '#289BF6',
    text: '#FFFFFF',
    gradientStart: '#289BF6',
    gradientEnd: '#289BF6',
  },

  bankDetails: {
    text: '#289BF6',
  },

  confirmation: {
    submitBg: '#289BF6',
    submitBorder: '#289BF6',
    cancelBg: '#121926',
    cancelBorder: '#364152',
  },

  offline: {
    primaryBg: 'rgb(255, 0, 34)',
    secondaryBorder: '#289BF6',
    secondaryText: '#289BF6',
  },

  checkedColor: '#289BF6',
  checkedTickColor: '#FFFFFF',

  secondary: '#4A90E2',
  secondaryLight: '#6BA8F0',
  secondaryDark: '#2E6BB8',

  success: '#4CAF50',
  successLight: '#2D4A2F',
  successDark: '#66BB6A',

  error: '#EF5350',
  errorLight: '#4A2D2E',
  errorDark: '#F44336',

  warning: '#FFB74D',
  warningLight: '#4A3D2D',
  warningDark: '#FFA726',
  warningRed: '#FF4466',

  info: '#29B6F6',
  infoLight: '#2D3D4A',
  infoBlue: '#289BF6',
  infoDark: '#03A9F4',

  dark: '#CDD5DF',
  grayDarkerFont: '#CDD5DF',
  grayDark: '#CDD5DF',
  gray: '#CDD5DF',
  grayLight: '#364152',
  grayLimitFont: '#CDD5DF',
  grayLighter: '#424242',
  light: '#2C2C2C',
  white: '#1E1E1E',

  text: {
    primary: '#CDD5DF',
    secondary: '#289BF6',
    light: '#FFFFFF',
    muted: '#CDD5DF',
    error: '#EF5350',
    label: '#CDD5DF',
    inputColor: '#CDD5DF',
  },

  bgPrimary: '#121926',
  bgSecondary: '#121926',
  bgDark: '#FFFFFF',
  bgLight: '#111827',
  accordionBg: '#0D121C',
};

// Select fallback defaults based on brand
const fallbackDefaults = env.brand === 'lf'
  ? lfBrandDefaults
  : env.brand === 'africabet'
  ? africaBetBrandDefaults
  : standardBrandDefaults;

export const darkColors: ColorPalette = {
  primary: env.dark.primary || fallbackDefaults.primary,
  primaryDark: env.dark.primaryDark || fallbackDefaults.primaryDark,
  primaryLight: env.dark.primaryLight || fallbackDefaults.primaryLight,
  checkedColor: env.dark.checkedColor || fallbackDefaults.checkedColor,
  checkedTickColor: env.dark.checkedTickColor || fallbackDefaults.checkedTickColor,

  gradient: {
    primaryStart: env.dark.gradientStart || fallbackDefaults.gradient.primaryStart,
    primaryEnd: env.dark.gradientEnd || fallbackDefaults.gradient.primaryEnd,
  },

  // Button colors
  button: {
    border: env.dark.buttonBorder || fallbackDefaults.button.border,
    text: env.dark.buttonText || fallbackDefaults.button.text,
    gradientStart: env.dark.buttonGradientStart || fallbackDefaults.button.gradientStart,
    gradientEnd: env.dark.buttonGradientEnd || fallbackDefaults.button.gradientEnd,
  },

  // Bank details colors
  bankDetails: {
    text: env.dark.bankDetailsText || fallbackDefaults.bankDetails.text,
  },

  // Confirmation page colors
  confirmation: {
    submitBg: env.dark.confirmationSubmitBg || fallbackDefaults.confirmation.submitBg,
    submitBorder: env.dark.confirmationSubmitBorder || fallbackDefaults.confirmation.submitBorder,
    cancelBg: env.dark.confirmationCancelBg || fallbackDefaults.confirmation.cancelBg,
    cancelBorder: env.dark.confirmationCancelBorder || fallbackDefaults.confirmation.cancelBorder,
  },

  // Offline modal colors
  offline: {
    primaryBg: env.dark.offlinePrimaryBg || fallbackDefaults.offline.primaryBg,
    secondaryBorder: env.dark.offlineSecondaryBorder || fallbackDefaults.offline.secondaryBorder,
    secondaryText: env.dark.offlineSecondaryText || fallbackDefaults.offline.secondaryText,
  },

  secondary: env.dark.secondary || fallbackDefaults.secondary,
  secondaryLight: env.dark.secondaryLight || fallbackDefaults.secondaryLight,
  secondaryDark: env.dark.secondaryDark || fallbackDefaults.secondaryDark,

  success: env.dark.success || fallbackDefaults.success,
  successLight: env.dark.successLight || fallbackDefaults.successLight,
  successDark: env.dark.successDark || fallbackDefaults.successDark,

  error: env.dark.error || fallbackDefaults.error,
  errorLight: env.dark.errorLight || fallbackDefaults.errorLight,
  errorDark: env.dark.errorDark || fallbackDefaults.errorDark,

  warning: env.dark.warning || fallbackDefaults.warning,
  warningLight: env.dark.warningLight || fallbackDefaults.warningLight,
  warningDark: env.dark.warningDark || fallbackDefaults.warningDark,
  warningRed: env.dark.warningRed || fallbackDefaults.warningRed,

  info: env.dark.info || fallbackDefaults.info,
  infoLight: env.dark.infoLight || fallbackDefaults.infoLight,
  infoBlue: env.dark.infoBlue || fallbackDefaults.infoBlue,
  infoDark: env.dark.infoDark || fallbackDefaults.infoDark,

  dark: env.dark.dark || fallbackDefaults.dark,
  grayDarkerFont: env.dark.grayDarkerFont || fallbackDefaults.grayDarkerFont,
  grayDark: env.dark.grayDark || fallbackDefaults.grayDark,
  gray: env.dark.gray || fallbackDefaults.gray,
  grayLight: env.dark.grayLight || fallbackDefaults.grayLight,
  grayLimitFont: env.dark.grayLimitFont || fallbackDefaults.grayLimitFont,
  grayLighter: env.dark.grayLighter || fallbackDefaults.grayLighter,
  light: env.dark.light || fallbackDefaults.light,
  white: env.dark.white || fallbackDefaults.white,

  text: {
    primary: env.dark.textPrimary || fallbackDefaults.text.primary,
    secondary: env.dark.textSecondary || fallbackDefaults.text.secondary,
    light: env.dark.textLight || fallbackDefaults.text.light,
    muted: env.dark.textMuted || fallbackDefaults.text.muted,
    error: env.dark.textError || fallbackDefaults.text.error,
    label: env.dark.textLabel || fallbackDefaults.text.label,
    inputColor: env.dark.textInputColor || fallbackDefaults.text.inputColor,
  },

  bgPrimary: env.dark.bgPrimary || fallbackDefaults.bgPrimary,
  bgSecondary: env.dark.bgSecondary || fallbackDefaults.bgSecondary,
  bgDark: env.dark.bgDark || fallbackDefaults.bgDark,
  bgLight: env.dark.bgLight || fallbackDefaults.bgLight,
  accordionBg: env.dark.accordionBg || fallbackDefaults.accordionBg,
};

export const africaBetDarkColors: ColorPalette = {
  primary: africaBetBrandDefaults.primary,
  primaryDark: africaBetBrandDefaults.primaryDark,
  primaryLight: africaBetBrandDefaults.primaryLight,
  checkedColor: africaBetBrandDefaults.checkedColor,
  checkedTickColor: africaBetBrandDefaults.checkedTickColor,

  gradient: {
    primaryStart: africaBetBrandDefaults.gradient.primaryStart,
    primaryEnd: africaBetBrandDefaults.gradient.primaryEnd,
  },

  button: {
    border: africaBetBrandDefaults.button.border,
    text: africaBetBrandDefaults.button.text,
    gradientStart: africaBetBrandDefaults.button.gradientStart,
    gradientEnd: africaBetBrandDefaults.button.gradientEnd,
  },

  bankDetails: {
    text: africaBetBrandDefaults.bankDetails.text,
  },

  confirmation: {
    submitBg: africaBetBrandDefaults.confirmation.submitBg,
    submitBorder: africaBetBrandDefaults.confirmation.submitBorder,
    cancelBg: africaBetBrandDefaults.confirmation.cancelBg,
    cancelBorder: africaBetBrandDefaults.confirmation.cancelBorder,
  },

  offline: {
    primaryBg: africaBetBrandDefaults.offline.primaryBg,
    secondaryBorder: africaBetBrandDefaults.offline.secondaryBorder,
    secondaryText: africaBetBrandDefaults.offline.secondaryText,
  },

  secondary: africaBetBrandDefaults.secondary,
  secondaryLight: africaBetBrandDefaults.secondaryLight,
  secondaryDark: africaBetBrandDefaults.secondaryDark,

  success: africaBetBrandDefaults.success,
  successLight: africaBetBrandDefaults.successLight,
  successDark: africaBetBrandDefaults.successDark,

  error: africaBetBrandDefaults.error,
  errorLight: africaBetBrandDefaults.errorLight,
  errorDark: africaBetBrandDefaults.errorDark,

  warning: africaBetBrandDefaults.warning,
  warningLight: africaBetBrandDefaults.warningLight,
  warningDark: africaBetBrandDefaults.warningDark,
  warningRed: africaBetBrandDefaults.warningRed,

  info: africaBetBrandDefaults.info,
  infoLight: africaBetBrandDefaults.infoLight,
  infoBlue: africaBetBrandDefaults.infoBlue,
  infoDark: africaBetBrandDefaults.infoDark,

  dark: africaBetBrandDefaults.dark,
  grayDarkerFont: africaBetBrandDefaults.grayDarkerFont,
  grayDark: africaBetBrandDefaults.grayDark,
  gray: africaBetBrandDefaults.gray,
  grayLight: africaBetBrandDefaults.grayLight,
  grayLimitFont: africaBetBrandDefaults.grayLimitFont,
  grayLighter: africaBetBrandDefaults.grayLighter,
  light: africaBetBrandDefaults.light,
  white: africaBetBrandDefaults.white,

  text: {
    primary: africaBetBrandDefaults.text.primary,
    secondary: africaBetBrandDefaults.text.secondary,
    light: africaBetBrandDefaults.text.light,
    muted: africaBetBrandDefaults.text.muted,
    error: africaBetBrandDefaults.text.error,
    label: africaBetBrandDefaults.text.label,
    inputColor: africaBetBrandDefaults.text.inputColor,
  },

  bgPrimary: africaBetBrandDefaults.bgPrimary,
  bgSecondary: africaBetBrandDefaults.bgSecondary,
  bgDark: africaBetBrandDefaults.bgDark,
  bgLight: africaBetBrandDefaults.bgLight,
  accordionBg: africaBetBrandDefaults.accordionBg,
};

